import type { RegisteredHandler, UserKeyboardShortcut } from "@/models/options";

// ---------------------------------------------------------------------------
// Singleton registry – one native `keydown` listener, O(1) combo lookup
// ---------------------------------------------------------------------------

/** combo key (e.g. "altKey+metaKey+d") → handler list */
const comboRegistry = new Map<string, RegisteredHandler[]>();

/** sequence key (e.g. "g,n") → handler list */
const sequenceRegistry = new Map<string, RegisteredHandler[]>();

/** actionName → combo registry key */
const actionToComboKey = new Map<string, string>();

/** actionName → sequence registry key */
const actionToSequenceKey = new Map<string, string>();

/** Pre-computed valid sequence prefixes for O(1) prefix check */
const sequencePrefixes = new Set<string>();

/** All registered handlers by id – for cleanup */
const allHandlers = new Map<symbol, RegisteredHandler>();

// Sequence state
let sequenceBuffer: string[] = [];
let sequenceTimer: ReturnType<typeof setTimeout> | null = null;
const SEQUENCE_TIMEOUT = 1000; // 1s between keys

// ---------------------------------------------------------------------------
// Normalisation helpers
// ---------------------------------------------------------------------------

/**
 * Build a deterministic combo key from modifier flags + a primary key.
 * Modifiers are the `KeyboardEvent` property names ("altKey", "ctrlKey", …)
 * sorted alphabetically so order never matters.
 */
export function normalizeComboKey({
  modifiers,
  key,
}: {
  modifiers: string[];
  key: string;
}): string {
  const sorted = [...modifiers].sort();
  return [...sorted, normalizeKey(key)].join("+");
}

/**
 * Normalise a key string so that both backend defaults (e.g. "left")
 * and browser KeyboardEvent values (e.g. "ArrowLeft") resolve to the
 * same lowercase form ("left").
 */
function normalizeKey(key: string): string {
  const lower = key.toLowerCase();
  if (lower.startsWith("arrow")) {
    return lower.replace("arrow", "");
  }
  return lower;
}

/**
 * Derive the same combo key from a live `KeyboardEvent`.
 * Inlined string concatenation avoids array allocations on the hot path.
 * Modifiers are appended in alphabetical order to match `normalizeComboKey`.
 */
function eventToComboKey(e: KeyboardEvent): string {
  let key = "";
  if (e.altKey) {
    key += "altKey+";
  }
  if (e.ctrlKey) {
    key += "ctrlKey+";
  }
  if (e.metaKey) {
    key += "metaKey+";
  }
  if (e.shiftKey) {
    key += "shiftKey+";
  }
  key += normalizeKey(e.key);
  return key;
}

// ---------------------------------------------------------------------------
// Single native listener
// ---------------------------------------------------------------------------

let listenerInstalled = false;

function handleKeyDown(e: KeyboardEvent): void {
  const hasModifiers = e.ctrlKey || e.altKey || e.shiftKey || e.metaKey;

  // --- 1. Try combo match (shortcuts with modifiers, or single keys) ---
  const comboKey = eventToComboKey(e);
  const comboHandlers = comboRegistry.get(comboKey);
  if (comboHandlers?.length) {
    for (const handler of comboHandlers) {
      if (handler.canPerform()) {
        if (hasModifiers) {
          e.preventDefault();
        }
        handler.callback();
        // Reset sequence buffer on combo match
        resetSequence();
        return;
      }
    }
  }

  // --- 2. Try sequence match (multi-key like "g then n") ---
  if (!hasModifiers && sequenceRegistry.size > 0) {
    handleSequenceKey(e);
  }
}

function handleSequenceKey(e: KeyboardEvent): void {
  const key = normalizeKey(e.key);

  // Reset timer
  if (sequenceTimer) {
    clearTimeout(sequenceTimer);
  }

  sequenceBuffer.push(key);
  const bufferStr = sequenceBuffer.join(",");

  // Check for exact match
  const handlers = sequenceRegistry.get(bufferStr);
  if (handlers?.length) {
    for (const handler of handlers) {
      if (handler.canPerform()) {
        handler.callback();
        resetSequence();
        return;
      }
    }
    // All handlers exhausted (none could perform) – reset
    resetSequence();
    return;
  }

  // O(1) prefix check using pre-computed Set
  if (!sequencePrefixes.has(bufferStr)) {
    // Not a prefix and not a match – reset
    resetSequence();
    return;
  }

  // Start timeout – reset if no next key within window
  sequenceTimer = setTimeout(resetSequence, SEQUENCE_TIMEOUT);
}

function resetSequence(): void {
  sequenceBuffer = [];
  if (sequenceTimer) {
    clearTimeout(sequenceTimer);
    sequenceTimer = null;
  }
}

function ensureListener(): void {
  if (listenerInstalled) {
    return;
  }
  document.addEventListener("keydown", handleKeyDown);
  listenerInstalled = true;
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Register a handler for a given action name.
 * The handler is placed in whichever registry key (combo or sequence)
 * is currently mapped to that action.
 *
 * Returns a cleanup function.
 */
export function registerHandler({
  actionName,
  callback,
  canPerform,
}: {
  actionName: string;
  callback: () => void;
  canPerform: () => boolean;
}): () => void {
  ensureListener();

  const handler: RegisteredHandler = {
    id: Symbol(actionName),
    actionName,
    callback,
    canPerform,
  };

  allHandlers.set(handler.id, handler);

  // Place into the correct registry
  placeHandler(handler);

  return () => {
    removeHandler(handler);
    allHandlers.delete(handler.id);
  };
}

function placeHandler(handler: RegisteredHandler): void {
  const comboKey = actionToComboKey.get(handler.actionName);
  if (comboKey !== undefined) {
    const list = comboRegistry.get(comboKey) ?? [];
    list.push(handler);
    comboRegistry.set(comboKey, list);
    return;
  }

  const seqKey = actionToSequenceKey.get(handler.actionName);
  if (seqKey !== undefined) {
    const list = sequenceRegistry.get(seqKey) ?? [];
    list.push(handler);
    sequenceRegistry.set(seqKey, list);
  }
}

/** O(1) removal using actionName → registry key lookup. */
function removeHandler(handler: RegisteredHandler): void {
  const comboKey = actionToComboKey.get(handler.actionName);
  if (comboKey !== undefined) {
    const list = comboRegistry.get(comboKey);
    if (!list) {
      return;
    }
    const idx = list.indexOf(handler);
    if (idx === -1) {
      return;
    }
    list.splice(idx, 1);
    if (list.length === 0) {
      comboRegistry.delete(comboKey);
    }
    return;
  }

  const seqKey = actionToSequenceKey.get(handler.actionName);
  if (seqKey === undefined) {
    return;
  }
  const list = sequenceRegistry.get(seqKey);
  if (!list) {
    return;
  }
  const idx = list.indexOf(handler);
  if (idx === -1) {
    return;
  }
  list.splice(idx, 1);
  if (list.length === 0) {
    sequenceRegistry.delete(seqKey);
  }
}

// ---------------------------------------------------------------------------
// Config sync – called when user shortcuts change (store update / customize)
// ---------------------------------------------------------------------------

/**
 * Rebuild the action→key lookup maps from shortcut configs,
 * then reindex all existing handlers into the new keys.
 */
export function updateShortcutConfigs(configs: UserKeyboardShortcut[]): void {
  actionToComboKey.clear();
  actionToSequenceKey.clear();
  sequencePrefixes.clear();

  for (const cfg of configs) {
    if (cfg.keys.length > 1) {
      // Sequence shortcut
      const seqKey = cfg.keys.map((k) => normalizeKey(k)).join(",");
      actionToSequenceKey.set(cfg.action_name, seqKey);
      // Pre-compute all prefixes for O(1) check in handleSequenceKey
      const parts = cfg.keys.map((k) => normalizeKey(k));
      for (let i = 1; i < parts.length; i++) {
        sequencePrefixes.add(parts.slice(0, i).join(","));
      }
    } else {
      // Combo shortcut (possibly with modifiers)
      const comboKey = normalizeComboKey({
        modifiers: cfg.modifiers ?? [],
        key: cfg.keys[0],
      });
      actionToComboKey.set(cfg.action_name, comboKey);
    }
  }

  reindexHandlers();
}

/**
 * Move all existing handlers into the correct registry buckets
 * based on current action→key mappings.
 */
function reindexHandlers(): void {
  // Clear registries but keep handlers
  comboRegistry.clear();
  sequenceRegistry.clear();

  for (const handler of allHandlers.values()) {
    placeHandler(handler);
  }
}

// ---------------------------------------------------------------------------
// Teardown – for tests and HMR
// ---------------------------------------------------------------------------

/**
 * Fully reset all singleton state. Call from test `afterEach` or HMR dispose.
 */
export function destroyRegistry(): void {
  resetSequence();
  comboRegistry.clear();
  sequenceRegistry.clear();
  actionToComboKey.clear();
  actionToSequenceKey.clear();
  sequencePrefixes.clear();
  allHandlers.clear();
  if (listenerInstalled) {
    document.removeEventListener("keydown", handleKeyDown);
    listenerInstalled = false;
  }
}
